// Copyright (c) 2024. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import React from 'react';
import './DeliveryCard.scss';
import { useNavigate } from 'react-router-dom';
import { fetchImageThumbnailUrl } from '../../shared_components/utils/ImgUtils';
import { format } from 'date-fns';
import { storage } from '../../context/firebase';
import { isMobileOnly } from 'react-device-detect';

import photoIcon from '../../assets/icons/white/image.png';
import balesIcon from '../../assets/icons/white/bales_stacked.png';
import alertIcon from '../../assets/icons/red/alert.png';
import materialIcon from '../../assets/icons/white/piechart.png';
import licensePlateIcon from '../../assets/icons/white/delivery.png';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { DELIVERY_ROUTE } from '../../constant/routes';
import { useAuthStore } from '../../stores/authStore';
import { getLabelDisplayNameMatchingGroup } from '../../shared_components/utils/FirebaseLabelsetsUtils';
import { useTranslation } from 'react-i18next';

export default function DeliveryCard({
  delivery,
  deliveriesPageData,
  lastSnapshot,
  setDeliveriesScrollStateTimeStamp,
}) {
  const { i18n } = useTranslation();
  const stateObj = { delivery_id: delivery.id, mode: 'delivery_overview' };
  const globalLabelsets = useAuthStore((state) => state.globalLabelsets);
  const suppliers = useAuthStore((state) => state.suppliers);
  const navigate = useNavigate();

  const [deliveryImg, setDeliveryImg] = React.useState('');
  const [isLoaded, setIsLoaded] = React.useState(false);

  const baseMaterialDisplayName = getLabelDisplayNameMatchingGroup(
    delivery.delivery_labels,
    globalLabelsets,
    'materials',
    i18n.language,
  );
  let supplierName = suppliers?.[delivery.supplier_id]?.display_name ?? delivery?.supplier_id;
  // HOTFIX: Show MOLE data when supplier name is not available
  if (!supplierName || supplierName === '') {
    supplierName = delivery?.MOLE_data?.model_eye_in_registered?.carrier_creditor_name;
  }

  React.useEffect(() => {
    if (!delivery?.delivery_images || Object.keys(delivery.delivery_images).length === 0) return;

    const sortedImages = Object.entries(delivery.delivery_images)
      .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
      .map(([, value]) => value);
    const imageStoragePath = sortedImages[0]?.image_storage_path;
    if (!imageStoragePath) return;

    fetchImageThumbnailUrl(storage, imageStoragePath).then((url) => {
      setDeliveryImg(url);
    });
  }, [delivery.id]);

  function getAdditionalIcons() {
    return (
      <div className='additional-icons'>
        {delivery?.claim_data?.claim_status && <img src={alertIcon} />}
        {delivery?.delivery_input_completed && <CheckCircleIcon className='delivery--check-icon' />}
      </div>
    );
  }

  function getDeliveryIcons(compact = false) {
    return (
      <div className={`delivery--icons ${compact ? 'compact' : ''}`}>
        {!compact && getAdditionalIcons()}
        <p>{Object.keys(delivery.delivery_images || {}).length}</p>
        <img src={photoIcon} />
        <p>{Object.keys(delivery.bales || {}).length}</p>
        <img src={balesIcon} />
        {compact && getAdditionalIcons()}
      </div>
    );
  }

  function handleNavigateToDelivery() {
    navigate(DELIVERY_ROUTE, {
      state: {
        ...stateObj,
        deliveriesPageData: {
          ...deliveriesPageData,
          scroll: deliveriesPageData.scrollRef.current.scrollTop,
          allLoaded: deliveriesPageData.allLoaded.current,
          lastSnapshot: lastSnapshot.current?.ref?.path,
          scrollRef: undefined,
        },
      },
    });
    setDeliveriesScrollStateTimeStamp(new Date());
  }
  return (
    <div onClick={handleNavigateToDelivery} className='delivery_card'>
      <div className='delivery--img-container'>
        {deliveryImg && (
          <img
            className='delivery--preview-img'
            src={deliveryImg}
            onLoad={() => setIsLoaded(true)}
            style={{ display: isLoaded ? 'block' : 'none' }}
          />
        )}
      </div>
      <div className={`delivery--datetime ${isMobileOnly ? 'compact' : ''}`}>
        <p className='delivery--date'>{delivery?.date && format(delivery.date.toDate(), 'dd.MM.yyyy')}</p>
        <p className='delivery--time'>{delivery?.date && format(delivery.date.toDate(), 'HH:mm')}</p>
        {isMobileOnly && getDeliveryIcons(true)}
      </div>
      <div className='delivery--responsive-container'>
        <p className='delivery--title'>{supplierName}</p>
        <div className='delivery--secondary-info'>
          {baseMaterialDisplayName && (
            <p className='delivery--secondary-info-text'>
              <img src={materialIcon} />
              {baseMaterialDisplayName}
            </p>
          )}
          {delivery?.documents_data?.complete_data?.vehicle_nr &&
            delivery?.documents_data?.complete_data?.vehicle_nr !== '' && (
              <p className='delivery--secondary-info-text'>
                <img src={licensePlateIcon} />
                {delivery?.documents_data?.complete_data?.vehicle_nr}
              </p>
            )}
        </div>
      </div>
      {!isMobileOnly && getDeliveryIcons()}
    </div>
  );
}
